import { useAppStore } from '@store'
import type { Object3D, Texture, WebGLRenderer, Scene } from 'three'
import { getTextureCacheMemoryEstimate } from '@utils/sceneObjects/loadedTextureCache'

if (typeof window !== 'undefined') {
  const w = window as Window & { __SCENE_COST_MODULE__?: boolean }
  w.__SCENE_COST_MODULE__ = true
  // console.log('[SceneCost] módulo cargado — tras abrir un proyecto busca [SceneCost] LISTO')
}

export type SceneCostPhase = {
  name: string
  ms: number
}

export type SceneCostGlbRow = {
  catalog: string
  url: string
  cacheHit: boolean
  bytes: number
  fetchMs: number
  parseMs: number
  apiMs: number
  meshes: number
  triangles: number
  embeddedTextures: number
  embeddedTexMB: number
}

export type SceneCostReport = {
  totalMs: number
  phases: SceneCostPhase[]
  itemsByType: Record<string, number>
  uniqueCatalogs: number
  furnitureInstances: number
  glbs: SceneCostGlbRow[]
  glbDownloadMB: number
  glbEmbeddedTexMB: number
  backendTextureUrls: number
  backendTextureCache: {
    count: number
    mb: number
    top: Array<{ url: string; w: number; h: number; mb: number; clampedFrom?: string }>
  }
  walls: number
  holes: number
  csgPairs: number
  gpu?: {
    geometries: number
    textures: number
    calls: number
    triangles: number
  }
  verdict: string[]
}

type Listener = (report: SceneCostReport | null) => void

let sessionStart = 0
let phaseName = ''
let phaseStart = 0
const phases: SceneCostPhase[] = []
const glbs: SceneCostGlbRow[] = []
let currentCatalog = ''
let currentApiMs = 0
const listeners = new Set<Listener>()
let lastReport: SceneCostReport | null = null
let rendererRef: WebGLRenderer | null = null
let sceneRef: Scene | null = null

export function registerSceneCostRenderer(gl: WebGLRenderer, scene: Scene) {
  rendererRef = gl
  sceneRef = scene
}

export function readSceneCostGpu(): SceneCostReport['gpu'] | undefined {
  try {
    if (!rendererRef) return undefined
    let triangles = rendererRef.info.render.triangles
    if (sceneRef) {
      triangles = 0
      sceneRef.traverse((obj) => {
        const mesh = obj as Object3D & {
          isMesh?: boolean
          geometry?: { index?: { count: number } | null; attributes?: { position?: { count: number } } }
        }
        if (!mesh.isMesh) return
        const geo = mesh.geometry
        if (geo?.index?.count) triangles += geo.index.count / 3
        else if (geo?.attributes?.position?.count) triangles += geo.attributes.position.count / 3
      })
    }
    return {
      geometries: rendererRef.info.memory.geometries,
      textures: rendererRef.info.memory.textures,
      calls: rendererRef.info.render.calls,
      triangles: Math.round(triangles),
    }
  } catch {
    return undefined
  }
}

function shortUrl(url: string) {
  try {
    const u = new URL(url, window.location.origin)
    return u.pathname.split('/').filter(Boolean).slice(-2).join('/') || url
  } catch {
    return url.slice(-48)
  }
}

function textureBytes(tex: Texture | null | undefined): number {
  if (!tex) return 0
  const img = tex.image as { width?: number; height?: number; videoWidth?: number; videoHeight?: number } | undefined
  const w = img?.width || img?.videoWidth || 0
  const h = img?.height || img?.videoHeight || 0
  if (!w || !h) return 0
  return w * h * 4 * (tex.generateMipmaps ? 1.33 : 1)
}

function analyzePrototype(root: Object3D | null | undefined) {
  let meshes = 0
  let triangles = 0
  let embeddedTextures = 0
  let embeddedTexMB = 0
  const seen = new Set<Texture>()
  if (!root || typeof root.traverse !== 'function') {
    return { meshes, triangles, embeddedTextures, embeddedTexMB }
  }
  root.traverse((node) => {
    const mesh = node as Object3D & {
      isMesh?: boolean
      geometry?: { index?: { count: number } | null; attributes?: { position?: { count: number } } }
      material?: { map?: Texture | null } | Array<{ map?: Texture | null }>
    }
    if (!mesh.isMesh) return
    meshes += 1
    const geo = mesh.geometry
    if (geo?.index?.count) triangles += geo.index.count / 3
    else if (geo?.attributes?.position?.count) triangles += geo.attributes.position.count / 3
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    for (const mat of mats) {
      const map = mat?.map
      if (!map || seen.has(map)) continue
      seen.add(map)
      embeddedTextures += 1
      embeddedTexMB += textureBytes(map) / (1024 * 1024)
    }
  })
  return { meshes, triangles, embeddedTextures, embeddedTexMB }
}

function endPhase() {
  if (!phaseName) return
  phases.push({ name: phaseName, ms: Math.round(performance.now() - phaseStart) })
  phaseName = ''
}

function publishSceneCost(report: SceneCostReport | { status: string; error?: string }) {
  if (typeof window === 'undefined') return
  const w = window as Window & {
    __sceneCost?: unknown
    __SCENE_COST__?: unknown
  }
  try {
    Object.defineProperty(w, '__sceneCost', {
      value: report,
      writable: true,
      configurable: true,
      enumerable: true,
    })
    Object.defineProperty(w, '__SCENE_COST__', {
      value: report,
      writable: true,
      configurable: true,
      enumerable: true,
    })
  } catch {
    w.__sceneCost = report
    w.__SCENE_COST__ = report
  }
}

export function beginSceneCostSession() {
  sessionStart = performance.now()
  phaseName = ''
  phaseStart = 0
  phases.length = 0
  glbs.length = 0
  currentCatalog = ''
  currentApiMs = 0
  lastReport = null
  publishSceneCost({ status: 'running' })
  listeners.forEach((fn) => fn(null))
}

export function markSceneCostPhase(name: string) {
  endPhase()
  phaseName = name
  phaseStart = performance.now()
}

export function setSceneCostCatalog(catalog: string, apiMs = 0) {
  currentCatalog = catalog
  currentApiMs = apiMs
}

export function recordSceneCostGlb(partial: {
  url: string
  cacheHit: boolean
  bytes: number
  fetchMs: number
  parseMs: number
  root?: Object3D | null
}) {
  const proto = analyzePrototype(partial.root)
  const existing = glbs.find((row) => row.url === partial.url)
  if (existing && partial.cacheHit) {
    existing.cacheHit = true
    return
  }
  glbs.push({
    catalog: currentCatalog || shortUrl(partial.url),
    url: partial.url,
    cacheHit: partial.cacheHit,
    bytes: partial.bytes,
    fetchMs: Math.round(partial.fetchMs),
    parseMs: Math.round(partial.parseMs),
    apiMs: Math.round(currentApiMs),
    ...proto,
  })
}

function countItems(items: Map<string, any>) {
  const itemsByType: Record<string, number> = {}
  let furnitureInstances = 0
  const catalogs = new Set<string>()
  let walls = 0
  let holes = 0
  const backendUrls = new Set<string>()

  for (const item of items.values()) {
    const type = String(item?.type || 'unknown')
    itemsByType[type] = (itemsByType[type] || 0) + 1
    if (type === 'walls' || type === 'partition' || type === 'hiddenWall') walls += 1
    if (type === 'holes') holes += 1
    if (type === 'items' || type === 'linkedItem' || type === 'linkedSon') {
      furnitureInstances += 1
      const key = String(item.catalogID || item.idUnidades || '')
      if (key) catalogs.add(key)
    }
    const textures = item?.textures
    if (Array.isArray(textures)) {
      for (const tex of textures) {
        const url = String(tex?.url || tex?.img || '').trim()
        if (url) backendUrls.add(url)
      }
    }
  }

  return {
    itemsByType,
    uniqueCatalogs: catalogs.size,
    furnitureInstances,
    walls,
    holes,
    backendTextureUrls: backendUrls.size,
  }
}

function buildVerdict(report: Omit<SceneCostReport, 'verdict'>): string[] {
  const lines: string[] = []
  const parseMs = report.phases.find((p) => p.name === 'glb-furniture')?.ms ?? 0
  const structureMs = report.phases.find((p) => p.name === 'glb-structure')?.ms ?? 0
  const remapMs = report.phases.find((p) => p.name === 'remap+textures')?.ms ?? 0
  const hydrateMs = report.phases.find((p) => p.name === 'hydrate')?.ms ?? 0
  const heaviestGlb = [...report.glbs].sort((a, b) => b.bytes - a.bytes)[0]
  const heaviestTex = report.backendTextureCache.top[0]

  if (parseMs + structureMs > report.totalMs * 0.45) {
    lines.push(
      `El open se va sobre todo en parse/descarga de GLB (${structureMs + parseMs} ms). Pesa el número de catálogos únicos (${report.uniqueCatalogs}), no las copias (${report.furnitureInstances}).`,
    )
  }
  if (report.glbDownloadMB > 40) {
    lines.push(
      `GLBs descargados ≈ ${report.glbDownloadMB.toFixed(1)} MB. Si el más gordo es «${heaviestGlb?.catalog}» (${((heaviestGlb?.bytes || 0) / 1024 / 1024).toFixed(1)} MB), es asset de catálogo, no JSON.`,
    )
  }
  if (report.glbEmbeddedTexMB > 30) {
    lines.push(
      `Texturas DENTRO de los GLB ≈ ${report.glbEmbeddedTexMB.toFixed(1)} MB GPU. Eso viene embebido en el .glb del back/CDN, no de textures[].`,
    )
  }
  if (report.backendTextureCache.mb > 20) {
    lines.push(
      `Texturas de acabado (API / textures[].url) ≈ ${report.backendTextureCache.mb.toFixed(1)} MB. ${heaviestTex ? `La más pesada ahora: ${heaviestTex.w}×${heaviestTex.h}${heaviestTex.clampedFrom ? ` (era ${heaviestTex.clampedFrom})` : ''}.` : ''}`,
    )
  }
  if (report.csgPairs > 40) {
    lines.push(
      `CSG: ${report.walls} paredes × ${report.holes} huecos (≈${report.csgPairs} pares). CPU al montar muros, no descarga.`,
    )
  }
  if (remapMs + hydrateMs > 800) {
    lines.push(`Post-GLB (remap/texturas/hydrate) ${remapMs + hydrateMs} ms: trabajo JS de zonas/acabados, no GPU todavía.`)
  }
  if (report.gpu && report.gpu.triangles > 1_500_000) {
    lines.push(
      `La escena montada tiene ${Math.round(report.gpu.triangles / 1000)}k triángulos y ${report.gpu.calls} draw calls. Eso es runtime 3D (clones + CSG + cotas), no el JSON.`,
    )
  }
  if (lines.length === 0) {
    lines.push('Esta escena no es extrema. Mira la tabla de GLB: el tiempo dominante suele ser parse de catálogos únicos.')
  }
  return lines
}

export function endSceneCostSession(gpu?: SceneCostReport['gpu']) {
  try {
    endPhase()
    const state = useAppStore.getState()
    const counted = countItems(state.items ?? new Map())
    let backendTextureCache = { count: 0, mb: 0, top: [] as SceneCostReport['backendTextureCache']['top'] }
    try {
      backendTextureCache = getTextureCacheMemoryEstimate()
    } catch {
      // ignore
    }
    const glbDownloadMB = glbs.reduce((acc, row) => acc + row.bytes, 0) / (1024 * 1024)
    const glbEmbeddedTexMB = glbs.reduce((acc, row) => acc + row.embeddedTexMB, 0)

    const report: SceneCostReport = {
      totalMs: Math.round(performance.now() - sessionStart),
      phases: [...phases],
      ...counted,
      glbs: [...glbs].sort((a, b) => b.bytes - a.bytes),
      glbDownloadMB,
      glbEmbeddedTexMB,
      backendTextureCache,
      csgPairs: counted.walls * counted.holes,
      gpu,
      verdict: [],
    }
    report.verdict = buildVerdict(report)
    lastReport = report
    publishSceneCost(report)
    logSceneCostReport(report)
    listeners.forEach((fn) => {
      try {
        fn(report)
      } catch {
        // ignore overlay errors
      }
    })
    return report
  } catch (error) {
    const failed = {
      status: 'error',
      error: error instanceof Error ? error.message : String(error),
    }
    publishSceneCost(failed)
    console.error('[SceneCost] no se pudo armar el informe', error)
    return null
  }
}

export function refreshSceneCostGpu() {
  if (!lastReport) return lastReport
  let backendTextureCache = lastReport.backendTextureCache
  try {
    backendTextureCache = getTextureCacheMemoryEstimate()
  } catch {
    // ignore
  }
  lastReport = {
    ...lastReport,
    gpu: readSceneCostGpu(),
    backendTextureCache,
  }
  lastReport.verdict = buildVerdict(lastReport)
  listeners.forEach((fn) => {
    try {
      fn(lastReport)
    } catch {
      // ignore
    }
  })
  publishSceneCost(lastReport)
  return lastReport
}

export function getLastSceneCostReport() {
  return lastReport
}

export function subscribeSceneCost(listener: Listener) {
  listeners.add(listener)
  listener(lastReport)
  return () => {
    listeners.delete(listener)
  }
}

function logSceneCostReport(_report: SceneCostReport) {
  // console.log(
  //   `[SceneCost] LISTO ${_report.totalMs} ms · ${_report.uniqueCatalogs} catálogos · ${_report.furnitureInstances} muebles · ${_report.glbDownloadMB.toFixed(1)} MB GLB — window.__sceneCost`,
  // )
}
